/**
 * Главный компонент приложения Larte Chat Widget
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import './styles/variables.css';
import './styles/global.css';
import './styles/animations.css';
import {
  ChatTriggerButton,
  ChatContainer,
  ChatHeader,
  MessagesList,
  InputArea,
  WelcomeScreen,
  Toast,
  ScreenTransition,
} from './components';
import { useN8nChat, useInitialization } from './hooks';
import { useChatStore } from './store/chatStore';
import { useUIStore } from './store/uiStore';
import { useConfigStore } from './store/configStore';
import { useToastStore } from './store/toastStore';
import { SuggestedQuestion } from './types/message.types';
import { analytics } from './utils/analytics';
import { isLauncherTriggerHidden, queryLauncherTrigger } from './utils/widgetHost';

/**
 * Корневой компонент виджета
 * 
 * @description Полноценный виджет с поддержкой Dify и n8n API
 */
export function App() {
  // Инициализация виджета (загрузка параметров из API)
  useInitialization();

  // Stores
  const { isOpen, showBadge } = useUIStore();
  const { messages, welcomeMessage, initialQuestions } = useChatStore();
  const { t, config } = useConfigStore();
  const { toasts, removeToast, showError: showToastError } = useToastStore();

  // Функции управления виджетом
  const handleToggle = () => {
    const wasOpen = useUIStore.getState().isOpen;
    useUIStore.getState().toggle();
    if (!wasOpen) analytics.chatOpened();
    else analytics.chatClosed();
  };

  const handleClose = () => {
    useUIStore.getState().close();
    analytics.chatClosed();
  };

  // Chat hook — только n8n (Dify не используется)
  const n8nChat = useN8nChat();
  const { sendMessage, isTyping, startNewConversation, isUploading } = n8nChat;
  
  // Прогресс загрузки из UI store
  const { uploadProgress } = useUIStore();

  // Позиция виджета из конфигурации
  const position = config.theme?.position || 'bottom-right';

  const widgetRootRef = useRef<HTMLDivElement>(null);
  const [launcherHidden, setLauncherHidden] = useState(false);

  const recalcLauncherVisibility = useCallback(() => {
    if (config.showLauncher === false) {
      setLauncherHidden(true);
      return;
    }
    const btn = queryLauncherTrigger(widgetRootRef.current);
    setLauncherHidden(isLauncherTriggerHidden(btn));
  }, [config.showLauncher]);

  useLayoutEffect(() => {
    recalcLauncherVisibility();
  }, [recalcLauncherVisibility, isOpen, messages.length]);

  useEffect(() => {
    const root = widgetRootRef.current;
    if (!root) return;

    const mo = new MutationObserver(() => {
      recalcLauncherVisibility();
    });
    mo.observe(root, {
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
    window.addEventListener('resize', recalcLauncherVisibility);

    const t1 = window.setTimeout(recalcLauncherVisibility, 100);
    const t2 = window.setTimeout(recalcLauncherVisibility, 500);

    return () => {
      mo.disconnect();
      window.removeEventListener('resize', recalcLauncherVisibility);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [recalcLauncherVisibility]);

  // Показываем welcome screen если нет сообщений
  const showWelcome = messages.length === 0;

  // Обработка клика на быстрый вопрос / action button
  const handleQuestionClick = (apiText: string, displayText?: string) => {
    analytics.quickQuestionClicked(displayText || apiText);
    sendMessage(apiText, undefined, displayText);
  };

  const handleSendMessage = (text: string, files?: File[]) => {
    analytics.messageSent(!!files?.length);
    if (files?.length) analytics.photoUploaded(files.length);
    sendMessage(text, files);
  };

  const handleBack = () => {
    analytics.newConversation();
    startNewConversation();
  };

  const handleFileDrop = (file: File) => {
    analytics.photoUploaded(1);
    sendMessage('', [file]);
  };

  // Колбеки при открытии/закрытии
  useEffect(() => {
    if (isOpen) {
      config.onOpen?.();
    } else {
      config.onClose?.();
    }
  }, [isOpen, config]);

  // Формируем suggested questions для WelcomeScreen
  const suggestedQuestions: SuggestedQuestion[] = initialQuestions.map((q, index) => ({
    id: `${index}`,
    text: q,
  }));

  return (
    <div id="larte-chat-widget-root" ref={widgetRootRef}>
      {/* Toast уведомления */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}

      {/* Встроенная кнопка триггера (можно отключить: showLauncher: false) */}
      {config.showLauncher !== false && (
        <ChatTriggerButton
          onClick={handleToggle}
          showBadge={showBadge && !isOpen}
          position={position}
          pulse={!isOpen && messages.length === 0}
        />
      )}

      {/* Контейнер виджета */}
      <ChatContainer
        isOpen={isOpen}
        position={position}
        launcherHidden={launcherHidden}
        onClose={handleClose}
        onFileDrop={handleFileDrop}
      >
        {/* Header */}
        <ChatHeader
          title={t('header_title') || 'LARTE AI Assistant'}
          isOnline={true}
          statusText={t('status_online') || 'Онлайн'}
          onClose={handleClose}
          showContacts={config.showContacts !== false}
          showBackButton={!showWelcome}
          onBack={handleBack}
        />

        {/* Welcome screen или Messages с плавным переходом */}
        <ScreenTransition transitionKey={showWelcome ? 'welcome' : 'chat'}>
          {showWelcome ? (
            <WelcomeScreen
              message={welcomeMessage || t('welcome_message') || 'Здравствуйте! Чем могу помочь?'}
              questions={suggestedQuestions}
              onQuestionClick={handleQuestionClick}
            />
          ) : (
            <MessagesList messages={messages} isTyping={isTyping} onSendMessage={handleQuestionClick} />
          )}
        </ScreenTransition>

        {/* Input area */}
        <InputArea
          placeholder={t('input_placeholder') || 'Введите сообщение...'}
          onSend={handleSendMessage}
          disabled={isTyping || isUploading}
          showAttach={config.enableFileUpload !== false}
          showVoice={false}
          uploadProgress={uploadProgress}
          onError={showToastError}
        />
      </ChatContainer>
    </div>
  );
}

export default App;
